import React from 'react';

import { useAuth } from '../auth/useAuth';
import { DirtyNavigationProvider } from './dirtyNavigation';
import { NavigationHistoryProvider } from './NavigationHistoryProvider';
import { NavigationScrollProvider } from './NavigationScrollProvider';
import type { RouterBasename } from './types';

export interface NavigationScopeContextValue {
  scopeId: string;
  userId: number | 'anonymous';
  basename: RouterBasename;
  authEpoch: number;
}

export interface NavigationScopeProviderProps {
  basename: RouterBasename;
  children: React.ReactNode;
  pendingFallback?: React.ReactNode;
}

const NavigationScopeContext = React.createContext<NavigationScopeContextValue | null>(null);

export function NavigationScopeProvider({
  basename,
  children,
  pendingFallback = null,
}: NavigationScopeProviderProps) {
  const { user, authEpoch, bootstrapResolved } = useAuth();

  const scopeId = React.useMemo(() => [
    authEpoch,
    user?.id ?? 'anonymous',
    String(user?.role ?? '').toLowerCase(),
    user?.is_developer ? 1 : 0,
    user?.simulation?.active ? 1 : 0,
    user?.simulation?.role ?? '',
    user?.simulation?.target_employee_id ?? '',
    user?.simulation?.target_student_id ?? '',
    user?.simulation?.effects_policy ?? '',
    basename,
  ].join(':'), [authEpoch, basename, user]);

  const value = React.useMemo<NavigationScopeContextValue>(() => ({
    scopeId,
    userId: user?.id ?? 'anonymous',
    basename,
    authEpoch,
  }), [authEpoch, basename, scopeId, user?.id]);

  if (!bootstrapResolved) return <>{pendingFallback}</>;

  return (
    <NavigationScopeContext.Provider value={value}>
      <NavigationHistoryProvider scopeId={scopeId}>
        <DirtyNavigationProvider key={scopeId}>
          <NavigationScrollProvider>
            {children}
          </NavigationScrollProvider>
        </DirtyNavigationProvider>
      </NavigationHistoryProvider>
    </NavigationScopeContext.Provider>
  );
}

// Keep the private context inaccessible so every consumer gets a useful ownership error.
// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationScope(): NavigationScopeContextValue {
  const value = React.useContext(NavigationScopeContext);
  if (!value) {
    throw new Error('useNavigationScope must be used within NavigationScopeProvider');
  }
  return value;
}

// Shared controls may also be rendered in isolation by older consumers.
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalNavigationScope(): NavigationScopeContextValue | null {
  return React.useContext(NavigationScopeContext);
}
